/**
 * PERFOLAB — Configuración centralizada de versionado (SemVer + build técnico).
 *
 * Reglas:
 * - APP_VERSION: versión semántica visible para usuarios (ej. "1.0.0").
 * - BUILD_VERSION: identificador técnico del build (timestamp YYYYMMDDHHMMSS).
 *   Se mantiene para Sentry, auditoría, soporte y debugging.
 * - RELEASE_CHANNEL: production | staging | preview | development.
 *
 * Toda la app DEBE consumir estos valores. No hardcodear versiones en componentes.
 */

// __APP_VERSION__ viene de vite.config.ts con formato `${semver}+${buildId}`.
const RAW_VERSION: string =
  typeof __APP_VERSION__ !== "undefined" ? __APP_VERSION__ : "0.0.0";

const RAW_BUILD_DATE: string =
  typeof __BUILD_DATE__ !== "undefined" ? __BUILD_DATE__ : new Date().toISOString();

const RAW_COMMIT: string =
  typeof __COMMIT_HASH__ !== "undefined" ? __COMMIT_HASH__ : "dev";

function parseSemver(raw: string): { semver: string; build: string } {
  const [semver, build = ""] = raw.split("+");
  return { semver: semver || "0.0.0", build };
}

function deriveBuildId(): string {
  const { build } = parseSemver(RAW_VERSION);
  if (build) return build;
  // Fallback: derivar de buildDate
  try {
    return RAW_BUILD_DATE.replace(/[-:.TZ]/g, "").slice(0, 14);
  } catch {
    return "unknown";
  }
}

function detectChannel(): "production" | "staging" | "preview" | "development" {
  const mode = import.meta.env.MODE;
  if (typeof window !== "undefined") {
    const host = window.location.hostname;
    if (host === "localhost" || host === "127.0.0.1") return "development";
    if (host.includes("id-preview--") || host.includes("lovableproject.com"))
      return "preview";
    if (host === "perfolab.lovable.app" || host.includes("staging"))
      return "staging";
    return "production";
  }
  return (mode as "production" | "development") ?? "development";
}

/** Versión semántica visible al usuario. Ej. "1.0.0". */
export const APP_VERSION: string = parseSemver(RAW_VERSION).semver;

/** Identificador técnico del build (YYYYMMDDHHMMSS o commit). */
export const BUILD_VERSION: string = deriveBuildId();

/** Canal de despliegue actual. */
export const RELEASE_CHANNEL = detectChannel();

/** Commit corto de git. */
export const COMMIT_HASH: string = RAW_COMMIT;

/** Fecha ISO del build. */
export const BUILD_DATE: string = RAW_BUILD_DATE;

/** Label corto para usuarios. Ej. "PERFOLAB v1.0.0". */
export function getAppLabel(): string {
  return `PERFOLAB v${APP_VERSION}`;
}

/** Label técnico completo. Ej. "v1.0.0 (build 20260528050919)". */
export function getVersionLabel(): string {
  return `v${APP_VERSION} (build ${BUILD_VERSION})`;
}

/** Label técnico extendido con canal. Ej. "v1.0.0 • build 20260528050919 • Production". */
export function getTechnicalLabel(): string {
  const channel =
    RELEASE_CHANNEL.charAt(0).toUpperCase() + RELEASE_CHANNEL.slice(1);
  return `v${APP_VERSION} • build ${BUILD_VERSION} • ${channel}`;
}

/** Objeto plano para tags de Sentry / analytics. */
export function getVersionTags() {
  return {
    app_version: APP_VERSION,
    build_version: BUILD_VERSION,
    release_channel: RELEASE_CHANNEL,
    commit: COMMIT_HASH,
  };
}
